import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

/**
 * Landing page for the Emergent-managed Google Auth callback.
 * Reads #session_id=... from the URL fragment, exchanges it with the backend,
 * then redirects to the app root.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
 */
export default function AuthCallback() {
  const nav = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();
  const [error, setError] = useState("");

  useEffect(() => {
    const hash = location.hash || window.location.hash || "";
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    const sid = params.get("session_id");
    if (!sid) {
      nav("/login", { replace: true });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await api.post("/auth/google/session", { session_id: sid });
        // Wipe the fragment so a refresh doesn't try to re-process it.
        if (!cancelled) {
          window.history.replaceState({}, "", "/");
          await refresh();
          nav("/", { replace: true });
        }
      } catch (e) {
        if (!cancelled) {
          setError(
            e?.response?.data?.detail ||
              "Could not sign in with Google. Please try again."
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="min-h-screen w-full bg-[#0A0A0A] text-white flex items-center justify-center px-6"
      data-testid="auth-callback-page"
    >
      <div className="text-center max-w-md">
        {!error ? (
          <>
            <Loader2 className="w-10 h-10 animate-spin mx-auto mb-6 text-white/70" />
            <div className="label-eyebrow mb-2">signing you in</div>
            <div className="font-display text-2xl font-black tracking-tight">
              Finishing Google sign-in…
            </div>
          </>
        ) : (
          <>
            <div className="label-eyebrow mb-3" style={{ color: "#ffb097" }}>
              sign-in failed
            </div>
            <div className="font-display text-2xl font-black tracking-tight mb-4">
              {error}
            </div>
            <button
              onClick={() => nav("/login", { replace: true })}
              className="btn-pill focus-ring"
              data-testid="auth-callback-back-btn"
            >
              Back to login
            </button>
          </>
        )}
      </div>
    </div>
  );
}
